/**
 * A shade ramp for the palette entries `SHADES.DAT` marks **flat** — the one
 * lookup 3D COCKPIT's structure shading needs that the game does not ship.
 *
 * ## Why a face can be unshadeable and still need shading
 *
 * `SHADES.DAT` gives each `BARON.PAL` index a half-width, and a zero means
 * "draw this colour flat". That is the right answer for the original: a strut
 * two pixels wide at 320x200 does not need a gradient, and the three blacks
 * must never move. It is the wrong answer for a camera *inside* the aeroplane,
 * where the same strut is a hand's breadth from the eye and fills a glance.
 * Measured on the enhanced Camel: `BARON.PAL` index 120 — `#594534`, the metal
 * `airframe.py`'s `metal_of` gives the cabane struts, the flying wires, the
 * undercarriage and the tail bracing — carries **500 of LOD0's triangles**, its
 * nearest vertex sits 0.11 m from the pilot's eye, and it fills the whole
 * downward glance with one dead tone (287,000 px of a single colour in a
 * `?renderer=webgl` capture). No cosine can move it, because
 * `ramp-material.ts` skips the walk for a zero half-width.
 *
 * ## The method is `world/landcover-shading.ts`'s; the ladder is not, and why
 *
 * That module had exactly this problem for the fields — a landcover tone the
 * palette gives no run to walk — and solved it the way a 1990 artist with 256
 * colours did: multiply the tone by a luminance factor, then take the **nearest
 * real palette entry at that hue** (`recoverRamp`, 682df14, measured to hold
 * hue to 0.969-1.000). Its hue cone and its `luminanceOf` are imported here
 * rather than restated, because those are the method.
 *
 * What is **not** reused is that module's *ladder of factors*, and the reason is
 * a measurement rather than a preference. `recoverRamp` walks
 * `GROUND_RELIEF_RAMP`'s own eleven rungs, whose factors are
 * `0.451 … 0.718, 0.998 … 1.403` — a ladder with a hole in it, because the
 * palette has a real gap below the ground green (`terrain-palette.ts` §3.3 says
 * so). Applied to a dark cockpit brown at luminance 73 those dark rungs target
 * luminance **33 to 52**, and the darkest entry in that brown's whole hue family
 * is **58**. So every dark rung finds nothing, the recovered ramp comes out as
 * the tone plus four *brighter* entries, and `rampIndexAt`'s arm — the smaller
 * of the two sides — is **zero**: the walk cannot move at all. A ladder tuned to
 * one tone's neighbourhood is not a ladder; it is that tone's neighbourhood.
 *
 * `STRUCTURE_RAMP_SPAN` below is therefore a *geometric* ladder about the tone
 * itself, which is symmetric by construction and has the property the mapping
 * needs: the middle rung is factor 1, i.e. the tone, so an unlit face draws the
 * byte the bake gave it.
 *
 * ## The shape on the GPU
 *
 * A 256-wide, 32-tall RGBA texture: **one row per shade level, one column per
 * palette index**, holding the colour that index draws at that level. One
 * `NearestFilter` fetch replaces the shipped path's two, and the table is 32 KB
 * built once per document — the same standing `ramp-material.ts`'s own two
 * lookups have.
 */

import * as THREE from "three/webgpu";
import { SHADE_LEVEL_COUNT } from "@red-opus/formats";
import { luminanceOf, RAMP_HUE_CONE } from "../world/landcover-shading.ts";
import { PALETTE_ENTRY_COUNT, type BakedPaletteBody } from "./ramp-shade.ts";

/**
 * How far the ladder reaches either side of the tone, as a luminance ratio.
 *
 * 1.55, which is what the palette will actually give a cockpit brown: index
 * 120's hue family runs from luminance 58 to 244 about a tone at 73, so a
 * downward reach of 1/1.55 (47) finds the one darker entry there is and an
 * upward reach of 1.55 (113) stops well before the family turns into pale sand.
 * Reaching further does not find better colours, it finds *other* colours.
 */
export const STRUCTURE_RAMP_SPAN = 1.55;

/**
 * Rungs in a recovered structure ramp. **Odd**, so there is an exact middle and
 * the middle is factor 1 — the tone itself, drawn byte for byte when the face is
 * square to the light. Nine is the resolution at which a step of the ladder is
 * about the size of a step of `BARON.PAL`'s own runs; more rungs would ask the
 * palette for colours between entries it does not have and land twice on the
 * same one.
 */
export const STRUCTURE_RAMP_RUNGS = 9;

/** `0xRRGGBB` for a baked palette entry, which is the packed form `luminanceOf` works in. */
function packed(palette: BakedPaletteBody, index: number): number {
  const entry = palette.colors[index] ?? [0, 0, 0];
  return (((entry[0] as number) & 0xff) << 16) | (((entry[1] as number) & 0xff) << 8) | ((entry[2] as number) & 0xff);
}

/** Cosine between two colours as RGB vectors — the hue test `recoverRamp` uses, restated because it is not exported. */
function hueCosine(a: number, b: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const magnitude = Math.hypot(ar, ag, ab) * Math.hypot(br, bg, bb);
  return magnitude > 0 ? (ar * br + ag * bg + ab * bb) / magnitude : 0;
}

/**
 * One tone's ladder, as `STRUCTURE_RAMP_RUNGS` real palette entries, darkest
 * first, with the tone exactly in the middle.
 *
 * A rung the palette cannot answer keeps whatever the rung inside it found —
 * never an invented colour, and never a hole in the ladder — so the array is
 * always full length and the middle is always the tone. That is what lets the
 * level mapping be a fixed piece of arithmetic instead of a search.
 */
export function structureRamp(palette: readonly number[], tone: number): number[] {
  const centre = (STRUCTURE_RAMP_RUNGS - 1) / 2;
  const anchor = luminanceOf(tone) || 1;
  const step = Math.log(STRUCTURE_RAMP_SPAN) / centre;
  const bracket = (anchor * (STRUCTURE_RAMP_SPAN - 1 / STRUCTURE_RAMP_SPAN)) / ((STRUCTURE_RAMP_RUNGS - 1) * 2);
  const rungs: number[] = Array.from({ length: STRUCTURE_RAMP_RUNGS }, () => tone);
  for (let rung = 0; rung < STRUCTURE_RAMP_RUNGS; rung++) {
    if (rung === centre) continue;
    const target = anchor * Math.exp((rung - centre) * step);
    let best: number | undefined;
    let bestCosine = RAMP_HUE_CONE;
    for (const entry of palette) {
      if (Math.abs(luminanceOf(entry) - target) > bracket) continue;
      const cosine = hueCosine(entry, tone);
      if (cosine >= bestCosine) {
        bestCosine = cosine;
        best = entry;
      }
    }
    // Fall back **inwards**, toward the tone, so a ladder that runs out of
    // palette flattens at its ends rather than jumping to a colour that is not
    // this tone's.
    rungs[rung] = best ?? (rung < centre ? (rungs[rung + 1] ?? tone) : (rungs[rung - 1] ?? tone));
  }
  // The inward fallback for the dark side has to run from the middle outwards,
  // which the loop above cannot do in one pass.
  for (let rung = centre - 1; rung >= 0; rung--) if (rungs[rung] === tone && rungs[rung + 1] !== tone) rungs[rung] = rungs[rung + 1] as number;
  return rungs;
}

/**
 * Which rung a shade level draws — `shadeLevelFromCosine`'s 0..31 mapped onto
 * the ladder with level 16 (cosine 0) on the middle rung.
 *
 * Deliberately not `terrain-palette.ts`'s `rampIndexAt`, which clamps its reach
 * to the *shorter* of a ramp's two arms because a recovered terrain ramp can be
 * lopsided. This ladder cannot be: it is built symmetric, so both arms are
 * `centre` long and the arithmetic is the same either way.
 */
export function structureRampIndex(level: number): number {
  const centre = (STRUCTURE_RAMP_RUNGS - 1) / 2;
  const at = centre + Math.round(((level - 16) * centre) / 16);
  return Math.max(0, Math.min(STRUCTURE_RAMP_RUNGS - 1, at));
}

/**
 * The RGBA texels of the table, row-major by shade level.
 *
 * Exported for the test, which re-derives the whole thing from the real
 * `BARON.PAL` and checks the properties that matter: every texel is a palette
 * entry the game ships, level 16 is the tone itself, and the walk runs dark to
 * light.
 */
export function structureRampTexels(palette: BakedPaletteBody): Uint8Array {
  const texels = new Uint8Array(PALETTE_ENTRY_COUNT * SHADE_LEVEL_COUNT * 4);
  const entries = Array.from({ length: PALETTE_ENTRY_COUNT }, (_, index) => packed(palette, index));
  const unique = [...new Set(entries)];
  for (let index = 0; index < PALETTE_ENTRY_COUNT; index++) {
    const rungs = structureRamp(unique, entries[index] as number);
    for (let level = 0; level < SHADE_LEVEL_COUNT; level++) {
      const colour = rungs[structureRampIndex(level)] as number;
      const at = (level * PALETTE_ENTRY_COUNT + index) * 4;
      texels[at] = (colour >> 16) & 0xff;
      texels[at + 1] = (colour >> 8) & 0xff;
      texels[at + 2] = colour & 0xff;
      texels[at + 3] = 255;
    }
  }
  return texels;
}

/** The table as a texture. `NoColorSpace` for `ramp-material.ts`'s reason — these are raw game bytes. */
export function createStructureRampTexture(palette: BakedPaletteBody): THREE.DataTexture {
  const texture = new THREE.DataTexture(structureRampTexels(palette), PALETTE_ENTRY_COUNT, SHADE_LEVEL_COUNT, THREE.RGBAFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}
